import { useMemo } from 'react';
import { type AdSize, type GoogleAdsPlacement } from '@/api/tenants/googleAdsPlacements';
import { PlacementConfigForm } from '@/components/google-ads/PlacementConfigForm';
import type { Pickem } from '@/api/pickems/getPickems';

// Default per-pickem placement key.
const CONTEST_CARD_PLACEMENT_KEY: GoogleAdsPlacement['placementKey'] = 'PICKEM_CONTEST_CARD';

interface PickemGoogleAdProps {
  pickem: Pickem & {
    google_ad?: GoogleAdsPlacement | null;
  };
  placement: GoogleAdsPlacement | null;
  onPlacementChange: (placement: GoogleAdsPlacement | null) => void;
  isSaving?: boolean;
  /**
   * Which per-pickem placement this section edits. Defaults to the contest
   * card so existing usage is unchanged.
   */
  placementKey?: GoogleAdsPlacement['placementKey'];
  /** Section heading. */
  title?: string;
  /** Section helper copy. */
  description?: string;
  /** Config form label. */
  formLabel?: string;
  /** Config form description. */
  formDescription?: string;
  /**
   * Sizes to seed a brand-new placement with. Defaults to null (no sizes),
   * matching the contest-card behaviour.
   */
  defaultSizes?: AdSize[] | null;
}

export function PickemGoogleAd({
  placement,
  onPlacementChange,
  isSaving = false,
  placementKey = CONTEST_CARD_PLACEMENT_KEY,
  title = 'Contest Card Ad Placement',
  description = 'Configure the Google Ad placement that will be displayed on the pickem contest card. Changes will be saved when you save the pickem.',
  formLabel = 'Contest Card Placement',
  formDescription = 'Ad displayed on the pickem contest card',
  defaultSizes = null,
}: PickemGoogleAdProps) {
  const currentPlacement = useMemo(() => {
    if (placement) return placement;

    // Create new empty placement
    return {
      placementKey,
      enabled: false,
      sizes: defaultSizes,
      sizeMappings: null,
      targeting: null,
    };
  }, [placement, placementKey, defaultSizes]);

  const handlePlacementChange = (updatedPlacement: GoogleAdsPlacement) => {
    onPlacementChange(updatedPlacement);
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-4">
        <div className="mb-2">
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      <PlacementConfigForm
        placement={currentPlacement}
        label={formLabel}
        description={formDescription}
        onChange={handlePlacementChange}
        isSaving={isSaving}
        defaultExpanded={true}
      />
    </div>
  );
}
